// src/components/BannerStack.jsx
import React, { useContext } from 'react';
import { NavigationContext } from '../context/NavigationContext';
import { useWarnings } from '../context/WarningsContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faTimes } from '@fortawesome/free-solid-svg-icons';

const BannerStackItem = ({ label, isActive, isClosable, onClick, onClose, showArrow }) => {
  return (
    <div className="flex items-center">
      <div 
        role="button"
        onClick={onClick}
        className={`flex items-center px-3 py-1.5 rounded-md transition-colors duration-200 text-sm cursor-pointer ${
          isActive 
            ? 'bg-slate-600 dark:bg-slate-500 text-white hover:bg-slate-700 dark:hover:bg-slate-400' 
            : 'bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
        }`}
      >
        <span>{label}</span>
        {isClosable && (
          <div
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="ml-2 opacity-60 hover:opacity-100 focus:outline-none cursor-pointer"
            title="Close"
          >
            <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
          </div>
        )}
      </div>
      {showArrow && (
        <FontAwesomeIcon 
          icon={faChevronRight} 
          className="mx-2 text-slate-400 dark:text-slate-500 w-3 h-3"
        />
      )}
    </div>
  );
};

const BannerStack = () => {
  const { levels, goToLevel, closeLevel } = useContext(NavigationContext);
  const { showWarnings, setShowWarnings } = useWarnings();

  const handleBannerClick = (banner) => {
    goToLevel(banner.key);
  };

  const handleCloseBanner = (banner) => {
    const index = levels.findIndex((level) => level.key === banner.key);
    if (index > 0) {
      closeLevel(banner.key);
    }
  };

  if (levels.length === 0) return null;

  return (
    <div className="bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-slate-700">
      <div className="px-4 sm:px-6 lg:px-8 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1">
            <span className="text-sm text-slate-500 dark:text-slate-400 mr-2">Analysis Level:</span>
            {levels.map((banner, index) => (
              <BannerStackItem
                key={banner.key}
                label={banner.label}
                isActive={index === levels.length - 1}
                isClosable={banner.key !== 'report'}
                onClick={() => handleBannerClick(banner)}
                onClose={() => handleCloseBanner(banner)}
                showArrow={index < levels.length - 1}
              />
            ))}
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-slate-500 dark:text-slate-400">
              Show warnings
            </span>
            <button
              onClick={() => setShowWarnings(!showWarnings)}
              className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-blue-400 focus:ring-offset-1 ${
                showWarnings ? 'bg-blue-400' : 'bg-gray-200'
              }`}
            >
              <span
                className={`${
                  showWarnings ? 'translate-x-4' : 'translate-x-0.5'
                } inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerStack;
